// SDK Storage provider - single backend for all presentation management

import { Configuration, StorageApi } from '../sdk';
import { getAuthIdToken } from './authToken';
import { rejectInsecureCloudMutation } from './clientSecurity';
import type { StorageObjectResponse } from '../sdk';

export interface PresentationMeta {
  id: string;
  ownerId: string;
  title: string;
  isPublic: boolean;
  collaborators: string[];
  updatedAt: number;
}

export interface LoadedPresentation {
  meta: PresentationMeta;
  markdown: string;
}

export interface PresentationSummary {
  id: string;
  title: string;
  description?: string;
  createdAt: any;
  updatedAt: any;
  userId: string;
  isPublic: boolean;
  tags: string[];
  contentLength: number;
}

// ===== SDK STORAGE =====

/**
 * Keep browser traffic same-origin on the production presenter host.
 *
 * api-storage.arkturian.com ultimately resolves back to arkserver. Inside the
 * studio LAN that address is private, so Chromium's Local Network Access policy
 * blocks a public presenter page from fetching it directly. Nginx exposes the
 * exact Storage API below /storage-api; development and non-product consumers
 * retain the configured absolute SDK base.
 */
const sameOriginStorageBase =
  typeof window !== 'undefined' && window.location.hostname === 'presenter.arkturian.com'
    ? `${window.location.origin}/storage-api`
    : null;
const SDK_BASE_URL =
  sameOriginStorageBase ||
  (import.meta as any).env?.VITE_SDK_BASE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

function browserStorageUrl(url: string): string {
  if (!sameOriginStorageBase || typeof window === 'undefined') return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (
      parsed.hostname === 'api-storage.arkturian.com' ||
      parsed.hostname === 'api-storage.arkserver.arkturian.com'
    ) {
      return `${sameOriginStorageBase}${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // Preserve the original value; fetch will surface the useful URL error.
  }
  return url;
}

async function getSdkStorage(): Promise<StorageApi> {
  const idToken = await getAuthIdToken().catch(() => null);
  const headers: Record<string, string> = {};
  if (idToken) {
    headers.Authorization = `Bearer ${idToken}`;
  }
  const cfg = new Configuration({
    basePath: SDK_BASE_URL,
    baseOptions: { headers }
  });
  return new StorageApi(cfg);
}

function logSdkError(contextMsg: string, err: any, extra?: Record<string, any>) {
  const status = err?.response?.status;
  const statusText = err?.response?.statusText;
  const data = err?.response?.data;
  const headers = err?.response?.headers;
  try {
    console.error(`[SDK] ${contextMsg} failed`, {
      status,
      statusText,
      headers,
      dataPreview: typeof data === 'string' ? data.slice(0, 500) : data,
      basePath: SDK_BASE_URL,
      privilegedBrowserKey: false,
      ...extra
    });
  } catch {}
}

export const sdkStorageProvider = {
  // Save markdown presentation as a file to storage
  async savePresentationMarkdown(markdown: string, title: string, isPublic = false): Promise<{ id: number; fileUrl: string; objectKey: string; } > {
    return rejectInsecureCloudMutation();
  },
  // Save or update by filename + (optionally) owner email
  async saveOrUpdatePresentation(markdown: string, title: string, isPublic = false, ownerEmail?: string | null): Promise<{ id: number; fileUrl: string; objectKey: string; } > {
    return rejectInsecureCloudMutation();
  },
  // List my presentations by context
  async listMyPresentations(): Promise<PresentationSummary[]> {
    return rejectInsecureCloudMutation();
  },
  // Raw storage objects for advanced use (includes file_url)
  async listMyPresentationObjects(): Promise<StorageObjectResponse[]> {
    return rejectInsecureCloudMutation();
  },
  async listPublicPresentationObjects(): Promise<StorageObjectResponse[]> {
    const storage = await getSdkStorage();
    const res = await storage.listObjectsStorageListGet(false, 'presentation');
    return (res.data.items as StorageObjectResponse[]).filter(it => it.is_public);
  },
  // Load markdown by file_url
  async loadPresentationByFileUrl(fileUrl: string): Promise<string> {
    const r = await fetch(browserStorageUrl(fileUrl));
    if (!r.ok) throw new Error(`Failed to fetch markdown: ${r.status}`);
    const text = await r.text();
    // Reject HTML responses (happens when auth fails or dev server falls back to index.html)
    const trimmed = text.trim();
    if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html') || trimmed.startsWith('<script')) {
      throw new Error('Server returned HTML instead of markdown — check URL/auth');
    }
    return text;
  },
  // Replace existing storage object content by object id (admin/owner required)
  async savePresentationToObjectId(objectId: number, markdown: string, filenameHint?: string, isPublic?: boolean): Promise<{ id: number; fileUrl: string; objectKey: string; } > {
    return rejectInsecureCloudMutation();
  },
  // Load a public markdown object by ID without browser credentials.
  async loadPresentationByObjectId(objectId: number): Promise<{ filename: string; content: string }> {
    try {
      // Fetch metadata directly to avoid SDK auth issues
      const metaRes = await fetch(`${SDK_BASE_URL}/storage/objects/${objectId}`);
      if (!metaRes.ok) throw new Error(`Metadata fetch failed: ${metaRes.status}`);
      const meta = await metaRes.json();
      const fileUrl = meta?.file_url as string;
      const filename = meta?.original_filename || `object-${objectId}.md`;
      if (!fileUrl) throw new Error('No file_url for object');
      const content = await this.loadPresentationByFileUrl(fileUrl);
      return { filename, content };
    } catch (err) {
      logSdkError('loadPresentationByObjectId', err, { objectId });
      throw err;
    }
  },
  // Delete by storage object id
  async deletePresentationObject(objectId: number): Promise<void> {
    rejectInsecureCloudMutation();
  },
  // Toggle public visibility
  async togglePublic(objectId: number, isPublic: boolean): Promise<void> {
    rejectInsecureCloudMutation();
  },
  // Get storage stats for user profile
  async getUserStorageStats(): Promise<{ totalBytes: number; presentationCount: number; formattedSize: string }> {
    try {
      const objects = await this.listMyPresentationObjects();
      const totalBytes = objects.reduce((sum, o) => sum + (o.file_size_bytes || 0), 0);
      const formatBytes = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
      };
      return { totalBytes, presentationCount: objects.length, formattedSize: formatBytes(totalBytes) };
    } catch {
      return { totalBytes: 0, presentationCount: 0, formattedSize: '0 Bytes' };
    }
  },
  // Generic: resolve file_url for any storage object id
  async getFileUrlByObjectId(objectId: number): Promise<string> {
    const storage = await getSdkStorage();
    try {
      const metaRes = await storage.getObjectMetadataStorageObjectsObjectIdGet(objectId, undefined);
      const fileUrl = (metaRes.data as any)?.file_url as string;
      if (!fileUrl) throw new Error('No file_url for object');
      return browserStorageUrl(fileUrl);
    } catch (err: any) {
      logSdkError('getObjectMetadataStorageObjectsObjectIdGet(getFileUrlByObjectId)', err, { objectId });
      throw err;
    }
  }
};
